
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface LanguageSwitcherProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "minimal" | "glass";
  showAnimation?: boolean;
  style?: React.CSSProperties;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  className, 
  size = "md",
  variant = "default",
  showAnimation = true,
  style
}) => {
  const { i18n } = useTranslation();
  
  // Debug logging to verify i18n is working
  useEffect(() => {
    console.log('LanguageSwitcher mounted. Current language:', i18n.language);
    console.log('i18n isInitialized:', i18n.isInitialized);
    console.log('Available languages:', i18n.languages);
  }, [i18n]);
  
  // Get the current and alternative language
  const currentLang = i18n.language || 'en';
  const altLang = currentLang === "es" ? "en" : "es";
  const altLangFull = altLang === "en" ? "English" : "Español";
  
  const toggleLanguage = () => {
    const newLang = currentLang === "es" ? "en" : "es";
    
    console.log('Toggling language from', currentLang, 'to', newLang);
    
    // Use i18n.changeLanguage directly from the hook
    i18n.changeLanguage(newLang);
    
    // Save to localStorage
    try {
      localStorage.setItem('propmatch-language', newLang);
      
      // Update document attributes for better SEO and accessibility
      document.documentElement.setAttribute('lang', newLang);
      document.documentElement.setAttribute('dir', 'ltr');
      
      // Set metadata for search engines
      const metaLanguage = document.querySelector('meta[name="language"]');
      if (metaLanguage) {
        metaLanguage.setAttribute('content', newLang);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'language';
        meta.content = newLang;
        document.head.appendChild(meta);
      }
      
      console.log('Language changed to:', newLang);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
    
    // Show a toast notification for better user feedback with custom styling
    toast.success(
      `Language switched to ${newLang === "en" ? "English" : "Español"}`,
      {
        duration: 2000,
        className: "language-toast",
        position: "top-center"
      }
    );
  };

  // Size classes based on the size prop
  const sizeClasses = {
    sm: "px-2 py-1.5 text-xs",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-2.5 text-base"
  };

  // Variant classes
  const variantClasses = {
    default: "bg-gradient-to-r from-propmatch-blue/80 to-propmatch-blue-light/80 backdrop-blur-md text-white",
    minimal: "bg-black/30 backdrop-blur-sm text-white/90 hover:bg-black/40",
    glass: "bg-white/10 border border-white/20 backdrop-blur-md text-white/90 hover:bg-white/15"
  };

  // Animation variants for framer-motion
  const buttonVariants = {
    initial: { 
      scale: 0.95, 
      opacity: 0,
      y: -5
    },
    animate: { 
      scale: 1, 
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    },
    exit: {
      scale: 0.95,
      opacity: 0,
      transition: { duration: 0.2 }
    },
    hover: { 
      scale: 1.05,
      y: -2,
      boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: { 
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    }
  };

  // Custom animations for reduced-motion settings
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      // Apply simplified animations for accessibility
      document.documentElement.style.setProperty('--language-transition-duration', '0.1s');
    }
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.button
        onClick={toggleLanguage}
        className={cn(
          "glass-card flex items-center justify-center rounded-full",
          "shadow-lg hover:shadow-propmatch-blue/30",
          "language-backdrop-blur", 
          showAnimation ? "language-switcher-enter language-switcher-hover" : "",
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
        style={style}
        initial={showAnimation ? buttonVariants.initial : undefined}
        animate={showAnimation ? buttonVariants.animate : undefined}
        exit={showAnimation ? buttonVariants.exit : undefined}
        whileHover={showAnimation ? buttonVariants.hover : undefined}
        whileTap={showAnimation ? buttonVariants.tap : undefined}
        variants={buttonVariants}
        aria-label={`Switch to ${altLangFull}`}
      >
        <Globe 
          size={size === "sm" ? 16 : size === "md" ? 20 : 24} 
          className={cn(
            "mr-2",
            showAnimation && "group-hover:rotate-12 transition-transform duration-300"
          )} 
        />
        <span className="font-medium">{altLang.toUpperCase()}</span>
      </motion.button>
    </AnimatePresence>
  );
};

export default LanguageSwitcher;
